/**
 * The strip map's arithmetic (strip-map spec §3.2, §3.3, §3.6).
 *
 * Everything here is pure: no React, no DOM, no canvas. `StripMapCanvas`
 * owns the drawing and the events; `stripPng.ts` owns the offscreen export;
 * this module owns the colour, the downsample and the two mappings both of
 * them need — so a reviewer can assert the picture without a browser, and so
 * `scripts/station_strip.py` has an exact specification to be the twin of.
 *
 * SIGN, once: `dz > 0` is FILL and `dz < 0` is CUT, for every run kind.
 * Deviation runs reach here already normalised (`station_bins.py`), which is
 * why the polarity is asked of `cutFillConvention` with the
 * `'surfaceDifference'` kind — the same call, for the same reason, that
 * `bandGeometry.ts::bandColor` makes.
 */
import { signIsCut } from '@/lib/cutFillConvention';
import { LABEL_MIN_GAP_PX } from '@/lib/stationVolumes/chartLayout';
import { DESIGN_PLANE_PREFIX, levelName } from '@/lib/stationVolumes/sectionModel';

/** Which edge of the pane the alignment's LEFT side is drawn on. The dock and
 *  the PNG both use `'left-top'` (spec §3.1: "left of the alignment is the top
 *  of the strip"); the other member exists so the mapping is a parameter
 *  rather than a convention buried in two `nOffsets - 1 - j` expressions. */
export type StripYOrientation = 'left-top' | 'left-bottom';

/** A row-major `nStations x nOffsets` field. Row `i` is bin (or station line)
 *  `i`; column `j` is the offset column `offsetStartM + j*step`. NaN is no
 *  data. */
export interface StripMatrix {
  values: Float32Array;
  nStations: number;
  nOffsets: number;
}

/** The three inks the strip spends, as `#rrggbb`. From `useSvPalette()` in the
 *  dock (`cut`, `fill`, `zero`); copied as literals in the Python twin. */
export interface StripInks {
  cut: string;
  fill: string;
  neutral: string;
}

/** Plot margins: `left` carries the L / CL / R offset labels, `bottom` the
 *  chainage axis and the equation tags. */
export const STRIP_MARGIN = { top: 8, right: 12, bottom: 26, left: 44 } as const;

/** Height of the per-bin verdict row under the strip (spec §3.4). */
export const VERDICT_ROW_PX = 6;

/** The PNG's pixel-column cap (spec §5). Wider runs are max-abs pooled. */
export const MAX_STRIP_COLUMNS = 2000;

const LUT_ENTRIES = 256;

const clamp = (v: number, lo: number, hi: number) => (v < lo ? lo : v > hi ? hi : v);

/** `#rrggbb` -> `[r, g, b]`. Throws rather than guessing: every caller passes
 *  a palette constant, so a bad value is a programming error. */
const hexRgb = (hex: string): [number, number, number] => {
  const m = /^#([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) throw new Error(`stripModel: expected a #rrggbb ink, got ${JSON.stringify(hex)}`);
  const n = parseInt(m[1], 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};

const mix = (a: number, b: number, t: number) => Math.round(a + (b - a) * t);

/**
 * The colour of one cell: transparent for no data, the neutral inside the
 * tolerance band, and the diverging ramp beyond it — saturating at `±spanM`
 * rather than wrapping, so the deepest cut is never painted in the fill hue
 * (the objection `bandGeometry.ts` records for the 3D bands).
 *
 * With `toleranceM === 0` this degrades to exactly the plain diverging ramp,
 * which is what a run with no expectation gets.
 */
export function stripColorAt(
  v: number,
  toleranceM: number,
  spanM: number,
  inks: StripInks,
): [number, number, number, number] {
  if (!Number.isFinite(v)) return [0, 0, 0, 0];
  const neutral = hexRgb(inks.neutral);
  const tol = Math.max(0, toleranceM);
  const abs = Math.abs(v);
  if (abs <= tol) return [neutral[0], neutral[1], neutral[2], 255];
  const span = Math.max(spanM, tol + 1e-9);
  const t = clamp((abs - tol) / (span - tol), 0, 1);
  const end = hexRgb(signIsCut('surfaceDifference', v) ? inks.cut : inks.fill);
  return [mix(neutral[0], end[0], t), mix(neutral[1], end[1], t), mix(neutral[2], end[2], t), 255];
}

/**
 * A 256-entry RGBA lookup over `[-spanM, +spanM]`, rebuilt only when the ramp,
 * the tolerance or the span changes (spec §3.6). One `stripColorAt` per cell
 * on a 250 x 61 matrix is 15 000 hex parses per redraw; this is one 256-entry
 * table and an index.
 */
export function buildStripLut(toleranceM: number, spanM: number, inks: StripInks): Uint8ClampedArray {
  const out = new Uint8ClampedArray(LUT_ENTRIES * 4);
  const span = Math.max(spanM, 1e-9);
  for (let i = 0; i < LUT_ENTRIES; i++) {
    const v = -span + (i / (LUT_ENTRIES - 1)) * 2 * span;
    const [r, g, b, a] = stripColorAt(v, toleranceM, span, inks);
    out[i * 4] = r;
    out[i * 4 + 1] = g;
    out[i * 4 + 2] = b;
    out[i * 4 + 3] = a;
  }
  return out;
}

/** Where `v` lands in the LUT, or `-1` for no data (which the caller draws as
 *  transparent rather than as a colour). Clamps; see `stripColorAt`. */
export function stripLutIndex(v: number, spanM: number): number {
  if (!Number.isFinite(v)) return -1;
  const span = Math.max(spanM, 1e-9);
  return Math.round(((clamp(v, -span, span) / span + 1) / 2) * (LUT_ENTRIES - 1));
}

/**
 * Downsample `src` to `dstCount` columns, keeping the value of LARGEST
 * MAGNITUDE in each destination range (spec §3.6).
 *
 * NOT an average and NOT the browser's `drawImage` downscale: both make a
 * one-bin exceedance disappear into its neighbours, and an exceedance that
 * disappears is the one thing this picture exists to show. Ties keep the
 * first; a range with no finite value yields NaN. When `dstCount >= srcCount`
 * this is a straight copy — upsampling is the blit's job, at
 * `imageSmoothingEnabled = false`.
 *
 * TWIN of `scripts/station_strip.py::pool_max_abs`. Both are asserted on the
 * same fixture; change one and change the other.
 */
export function poolMaxAbs(
  src: ArrayLike<number>,
  srcCount: number,
  dstCount: number,
  out?: Float32Array,
): Float32Array {
  const n = Math.max(0, Math.min(srcCount, src.length));
  const d = dstCount >= n ? n : Math.max(1, dstCount);
  const dst = out && out.length >= d ? out.subarray(0, d) : new Float32Array(d);
  for (let j = 0; j < d; j++) {
    const from = Math.floor((j * n) / d);
    const to = j === d - 1 ? n : Math.floor(((j + 1) * n) / d);
    let best = Number.NaN;
    let bestAbs = -1;
    for (let i = from; i < to; i++) {
      const v = src[i];
      if (!Number.isFinite(v)) continue;
      const a = Math.abs(v);
      if (a > bestAbs) {
        bestAbs = a;
        best = v;
      }
    }
    dst[j] = best;
  }
  return dst;
}

/**
 * Symmetric p98 half-span over the finite absolute values — the same p98
 * INTERPOLATION `aggregate.ts::symmetricDomain` runs for the 3D bands, applied
 * to a DIFFERENT quantity (spec §3.3).
 *
 * Not the same number, and the difference matters: the bands take p98 of
 * `|net m³/m|` over the BINS (`StationBands.tsx`: `symmetricDomain(bins.map(
 * netPerMetre))`), the strip takes p98 of `|Δz|` over the CELLS, in metres.
 * Different quantity, different unit — the two scales are siblings in method,
 * never equal in value, and a maintainer "fixing" one to match the other
 * breaks the Python twin, which shares this quantity and not the bands'.
 *
 * Written here rather than reusing that function because it takes a
 * `number[]`: a 250 x 61 plane is 15 000 values and a 4 000-bin run is a
 * million, and `Array.from(...).map(Math.abs).sort()` on that allocates twice
 * and sorts with a comparator. Same interpolation, same never-zero floor —
 * the test asserts the two agree on one shared input.
 */
export function p98AbsFinite(values: ArrayLike<number>, percentile = 98): number {
  const buf = new Float64Array(values.length);
  let n = 0;
  for (let i = 0; i < values.length; i++) {
    const v = values[i];
    if (Number.isFinite(v)) buf[n++] = Math.abs(v);
  }
  if (n === 0) return 1;
  const abs = buf.subarray(0, n).sort();
  const idx = (percentile / 100) * (n - 1);
  const lo = Math.floor(idx);
  const hi = Math.ceil(idx);
  const p = abs[lo] + (abs[hi] - abs[lo]) * (idx - lo);
  return p || 1;
}

/**
 * Narrowest an on-screen mark is allowed to get, in the drawing space's own
 * pixels. TWIN of `station_strip.py::_MIN_MARK_PX`.
 *
 * Below ~2 px a hatch has no room for a single diagonal and the mark simply
 * is not painted. `MAX_BINS` is 20 000, so one bin in an 1 100 px plot is
 * 0.055 px: without this floor the low-coverage hatch and the verdict row
 * disappear on exactly the long runs that need them, while the legend goes on
 * promising "hatched: low coverage".
 *
 * Both client drawing spaces are CSS pixels scaled UP to device pixels (the
 * dock by `devicePixelRatio`, the PNG by `STRIP_PNG_SCALE = 2`), so a 3 here
 * is >= 3 device pixels in either — the Python's unit, conservatively.
 */
export const MIN_MARK_PX = 3;

/** One half-open `[start, end)` run of equal adjacent values, and the value. */
export interface ValueRun<T> {
  start: number;
  end: number;
  value: T;
}

/**
 * Adjacent equal values merged into half-open runs.
 *
 * Per RUN rather than per cell, for the same reason the Python twin does it:
 * a 20 000-bin run must not put 20 000 marks on a 1 100 px pane, which is slow
 * to lay out and no more informative than the handful of stripes it actually
 * is. Comparison is `Object.is`, so `null` merges with `null` and a `NaN`
 * never merges with anything.
 */
export function sameValueRuns<T>(values: ArrayLike<T>): ValueRun<T>[] {
  const runs: ValueRun<T>[] = [];
  for (let i = 0; i < values.length; i++) {
    const v = values[i];
    const last = runs[runs.length - 1];
    if (last && Object.is(last.value, v)) last.end = i + 1;
    else runs.push({ start: i, end: i + 1, value: v });
  }
  return runs;
}

/**
 * Adjacent low-coverage bins merged into half-open `[start, end)` runs.
 *
 * TWIN of `station_strip.py::low_coverage_runs`, same merge rule, same
 * half-open convention.
 */
export function lowCoverageRuns(flags: ArrayLike<boolean>): [number, number][] {
  const out: [number, number][] = [];
  let start = -1;
  for (let i = 0; i < flags.length; i++) {
    if (flags[i]) {
      if (start < 0) start = i;
    } else if (start >= 0) {
      out.push([start, i]);
      start = -1;
    }
  }
  if (start >= 0) out.push([start, flags.length]);
  return out;
}

/**
 * The x and width, in plot pixels, of a mark spanning bins `[start, end)` —
 * floored at `minPx` so it is still painted on a run whose bins are thinner
 * than a pixel.
 *
 * TWIN of the widening `station_strip.py::write_strip_png` applies to its
 * hatch patches: widened about the mark's OWN CENTRE, so a floored stripe
 * still points at the stations it is about, then clamped inside the plot box
 * so a mark at either end is not drawn off the picture.
 */
export function markSpanPx(
  start: number,
  end: number,
  nBins: number,
  plotLeft: number,
  plotWidth: number,
  minPx: number = MIN_MARK_PX,
): { x: number; width: number } {
  const n = Math.max(1, nBins);
  let width = ((end - start) / n) * plotWidth;
  let x = plotLeft + (start / n) * plotWidth;
  if (width < minPx) {
    x -= (minPx - width) / 2;
    width = minPx;
  }
  const maxX = plotLeft + Math.max(0, plotWidth - width);
  return { x: clamp(x, plotLeft, Math.max(plotLeft, maxX)), width };
}

/** The `ImageData` row that offset column `j` is drawn on. */
export function imageRowForOffsetIndex(
  j: number,
  nOffsets: number,
  orientation: StripYOrientation = 'left-top',
): number {
  return orientation === 'left-top' ? j : nOffsets - 1 - j;
}

/** The offset column under a pointer at `fy` (0 at the top of the strip box,
 *  1 at the bottom). Clamps — a drag that leaves the pane keeps reading the
 *  edge column rather than reporting nothing. */
export function offsetIndexAtFraction(
  fy: number,
  nOffsets: number,
  orientation: StripYOrientation = 'left-top',
): number {
  const row = clamp(Math.floor(fy * nOffsets), 0, nOffsets - 1);
  return orientation === 'left-top' ? row : nOffsets - 1 - row;
}

/** The bin (pixel column) under a pointer at `fx` (0 left, 1 right). Clamps. */
export function binIndexAtFraction(fx: number, nColumns: number): number {
  return clamp(Math.floor(fx * nColumns), 0, Math.max(0, nColumns - 1));
}

/** The offset a column is quoted AT — its centre, matching how
 *  `useStationVolumes` documents the `.f32` grid. */
export function offsetMetresAt(j: number, offsetStartM: number, offsetStepM: number): number {
  return offsetStartM + (j + 0.5) * offsetStepM;
}

/**
 * Spec §3.1 asks for >= 80 px between chainage labels — wider than the chart's
 * own `LABEL_MIN_GAP_PX` (64), because a strip carries no bar under a label to
 * separate it from its neighbour. Written as a floor over the chart's constant
 * so the two can never cross.
 *
 * Here rather than in either drawing module because the dock and the PNG must
 * thin their chainage axes IDENTICALLY: the export is meant to be the picture
 * the user was looking at, and a printed axis one label denser is a difference
 * nobody would read as a bug.
 */
export const STA_LABEL_MIN_GAP_PX = Math.max(80, LABEL_MIN_GAP_PX);

/**
 * Offset in metres -> y. Row 0 is the MOST NEGATIVE offset, and negative is
 * left of the alignment: left at the top (spec §3.1).
 *
 * The continuous twin of `imageRowForOffsetIndex`: that one places a pixel ROW,
 * this one places an arbitrary metre value (the swath rules, the reference
 * extent, the hover crosshair) on the same axis. They agree at the column
 * centres `offsetMetresAt` quotes — asserted in `stripModel.test.ts`, because
 * a disagreement would put a swath rule half a column off the data it bounds.
 */
export function yForOffsetM(
  offsetM: number,
  offsetStartM: number,
  offsetStepM: number,
  nOffsets: number,
  plotTop: number,
  plotHeight: number,
  orientation: StripYOrientation = 'left-top',
): number {
  const f = (offsetM - offsetStartM) / Math.max(1e-9, nOffsets * offsetStepM);
  return plotTop + (orientation === 'left-top' ? f : 1 - f) * plotHeight;
}

/**
 * Internal station -> x, across the WHOLE run's extent.
 *
 * Deliberately not `plotLeft + binIndex * colW`: a station equation lands
 * between bins, and a run with an equation has a chainage axis that is not the
 * bin axis. Anchored on `stationFromInternalM`/`stationToInternalM` so the
 * equation rules, their tags and the bin columns are drawn on one projection.
 * A zero- or negative-length run divides by 1 rather than by 0 — it draws
 * everything at `plotLeft`, which is the honest picture of a run with no
 * extent.
 */
export function xForStation(
  staInternalM: number,
  params: { stationFromInternalM: number; stationToInternalM: number },
  plotLeft: number,
  plotWidth: number,
): number {
  const lo = params.stationFromInternalM;
  const hi = params.stationToInternalM;
  return plotLeft + ((staInternalM - lo) / (hi > lo ? hi - lo : 1)) * plotWidth;
}

/**
 * The whole matrix as RGBA bytes at `columns x nOffsets`, one pass, ready for
 * `ctx.putImageData` into an offscreen canvas of exactly that size (spec
 * §3.6). The caller blits it scaled with `imageSmoothingEnabled = false`.
 */
export function buildStripPixels(
  m: StripMatrix,
  o: { columns: number; lut: Uint8ClampedArray; spanM: number; orientation?: StripYOrientation },
): { data: Uint8ClampedArray; width: number; height: number } {
  const width = Math.max(1, Math.min(o.columns, m.nStations) || 1);
  const height = Math.max(1, m.nOffsets);
  const data = new Uint8ClampedArray(width * height * 4);
  // One scratch column reused for every offset row: the matrix is row-major by
  // STATION, so the values a pixel column pools are strided, and `poolMaxAbs`
  // takes a contiguous run on purpose (its Python twin does too).
  const column = new Float32Array(m.nStations);
  const pooled = new Float32Array(width);
  for (let j = 0; j < m.nOffsets; j++) {
    for (let i = 0; i < m.nStations; i++) column[i] = m.values[i * m.nOffsets + j];
    poolMaxAbs(column, m.nStations, width, pooled);
    const row = imageRowForOffsetIndex(j, m.nOffsets, o.orientation);
    for (let x = 0; x < width; x++) {
      const px = (row * width + x) * 4;
      const k = stripLutIndex(pooled[x], o.spanM);
      if (k < 0) continue; // no data: leave it transparent
      data[px] = o.lut[k * 4];
      data[px + 1] = o.lut[k * 4 + 1];
      data[px + 2] = o.lut[k * 4 + 2];
      data[px + 3] = o.lut[k * 4 + 3];
    }
  }
  return { data, width, height };
}

// ---------------------------------------------------------------------------
// The multi-layer stack's strip (spec §3.6, ruling A-3).
// ---------------------------------------------------------------------------

/** Where a stack strip's design thickness came from — the two sources spec
 *  §3.6 allows, and `null` when there is none and the values are RAW. */
export type StackDesignSource = 'planes' | 'bins';

/** `stackThicknessMatrix`'s answer: the field to draw, and whether it is a
 *  deviation at all. */
export interface StackStripMatrix {
  matrix: StripMatrix;
  /** `false` means the values are the RAW thickness — the caller must switch
   *  the tolerance band OFF and say so. */
  designed: boolean;
  designSource: StackDesignSource | null;
}

/** Just enough of `StationSections` to compute a strip from. Structural so the
 *  tests can hand a two-plane fixture instead of a whole decoded file. */
export interface StackStripSections {
  planes: Record<string, Float32Array>;
  nStationLines: number;
  nOffsets: number;
}

/** Just enough of `StationBin`. A stack's bins reach here through the
 *  synthesised `StationVolumesJson` the dock builds for the selected layer. */
export interface StackStripBin {
  meanThicknessM?: number | null;
  meanDeviationM?: number | null;
}

/**
 * A stack layer's strip source (strip-map spec §3.6, A-3).
 *
 * A stack writes no `dz` plane — it writes `station_sections.f32`'s `2N`
 * as-built planes at the station LINES — so the strip's field is the layer's
 * own thickness, and where the run knows what that thickness was SUPPOSED to
 * be, its deviation from it: the same "deviation from expected" the section
 * pane hatches.
 *
 * ONE ROW PER BIN, taken from the bin's LEADING station line, and the final
 * line dropped. Every other mark on this pane — the brush, the verdict row,
 * the hover atom, the walk cursor — is bin-indexed, and interpolating two
 * lines into a bin would invent geometry the section pane deliberately
 * refuses to invent (`useStationVolumes.ts::sectionLineAt`). The tab states
 * the approximation rather than leaving it to be discovered.
 *
 * Two design sources, in this order:
 *
 * - `'planes'` — the layer's two DESIGN planes, per column, exactly as
 *   `sectionLayers` builds `expected` in SURFACES mode
 *   (`sectionModel.ts`: `dTop[j] - dBottom[j]`). This is the spec's "where a
 *   design plane exists", and it is per COLUMN, so a design cross-fall shows.
 *   A named-but-all-NaN plane is NOT a design: `stack_analysis.py` gives every
 *   declared level a plane and fills it with NaN when no design surface was
 *   supplied, and reading that as a target of 0 would paint a whole 100 mm
 *   layer 100 mm out of spec.
 * - `'bins'` — the scalar the bins imply, `meanThicknessM - meanDeviationM`.
 *   That is exactly how `stationExport.ts` reconstructs `design_thickness_m`
 *   for the CSV, so the strip and the exported table quote one number.
 *
 * `designed: false` means neither was available: the values are then the RAW
 * thickness and the caller must switch the tolerance band OFF — a band around
 * zero is meaningless against an unknown target.
 *
 * `null` when the plane pair is absent from the file, which is how a chip for
 * a PENDING layer arrives here.
 */
export function stackThicknessMatrix(
  sections: StackStripSections,
  planePair: readonly [string, string] | null,
  bins: readonly StackStripBin[],
): StackStripMatrix | null {
  if (!planePair) return null;
  const top = sections.planes[planePair[0]];
  const bottom = sections.planes[planePair[1]];
  if (!top || !bottom) return null;
  // The bin count and the line count both bound the picture: the run may carry
  // fewer bins than the file has lines (a PENDING tail), and a bin beyond the
  // file's lines has no geometry to draw at all.
  const nStations = Math.min(bins.length, Math.max(0, sections.nStationLines - 1));
  const nOffsets = sections.nOffsets;
  const n = nStations * nOffsets;
  const values = new Float32Array(n);

  // (a) The per-column design, from the two DESIGN planes.
  const designTop = sections.planes[DESIGN_PLANE_PREFIX + levelName(planePair[0])];
  const designBottom = sections.planes[DESIGN_PLANE_PREFIX + levelName(planePair[1])];
  let planeDesign: Float32Array | null = null;
  if (designTop && designBottom) {
    const e = new Float32Array(n);
    let anyFinite = false;
    for (let k = 0; k < n; k++) {
      e[k] = designTop[k] - designBottom[k];
      if (Number.isFinite(e[k])) anyFinite = true;
    }
    if (anyFinite) planeDesign = e;
  }

  // (b) The per-bin scalar the compliance table quotes.
  const binDesignOf = (i: number): number => {
    const b = bins[i];
    const m = b?.meanThicknessM;
    const d = b?.meanDeviationM;
    return typeof m === 'number' && Number.isFinite(m) && typeof d === 'number' && Number.isFinite(d) ? m - d : NaN;
  };
  let anyBinDesign = false;
  for (let i = 0; i < nStations && !anyBinDesign; i++) if (Number.isFinite(binDesignOf(i))) anyBinDesign = true;

  const designSource: StackDesignSource | null = planeDesign ? 'planes' : anyBinDesign ? 'bins' : null;

  for (let i = 0; i < nStations; i++) {
    // Resolved once per row: the bin scalar cannot change across a row, and
    // recomputing it per column is `nOffsets` property lookups for one number.
    const rowDesign = designSource === 'bins' ? binDesignOf(i) : 0;
    for (let j = 0; j < nOffsets; j++) {
      const k = i * nOffsets + j;
      const thickness = top[k] - bottom[k];
      values[k] =
        designSource === 'planes' ? thickness - planeDesign![k] : designSource === 'bins' ? thickness - rowDesign : thickness;
    }
  }
  return { matrix: { values, nStations, nOffsets }, designed: designSource !== null, designSource };
}

// ---------------------------------------------------------------------------
// Ramp-span slider scale
// ---------------------------------------------------------------------------

/** Slider positions. Integers, so the thumb lands somewhere reproducible. */
export const SPAN_SLIDER_STEPS = 1000;

/** How far either side of Auto the slider reaches, in decades. One decade
 *  covers ±10×, which spans every span anyone has wanted on real data. */
const SPAN_SLIDER_DECADES = 1;

/** Auto is the fallback when a run has no finite values to take a p98 from;
 *  it mirrors `useStripSettings`'s own default so the slider never divides by
 *  zero on an empty strip. */
const SPAN_SLIDER_AUTO_FALLBACK_M = 0.05;

const spanSliderBounds = (autoSpanM: number) => {
  const auto = Number.isFinite(autoSpanM) && autoSpanM > 0 ? autoSpanM : SPAN_SLIDER_AUTO_FALLBACK_M;
  const factor = 10 ** SPAN_SLIDER_DECADES;
  return { lo: auto / factor, hi: auto * factor };
};

/**
 * Where a span sits on the slider, 0…`SPAN_SLIDER_STEPS`.
 *
 * The scale is LOGARITHMIC and centred on Auto, so Auto is always the midpoint
 * and one step means the same proportional change everywhere on the track. A
 * linear track cannot work here: Δz spans orders of magnitude between a
 * wearing course and an earthworks run, so every useful value would bunch into
 * the first few pixels.
 *
 * A span outside the track's decade clamps to an end — the number field stays
 * the way to enter something the slider cannot reach, and the stored value is
 * never rewritten to match the thumb.
 */
export function spanSliderPosition(spanM: number, autoSpanM: number): number {
  const { lo, hi } = spanSliderBounds(autoSpanM);
  if (!Number.isFinite(spanM) || spanM <= 0) return 0;
  const t = Math.log(spanM / lo) / Math.log(hi / lo);
  return Math.round(clamp(t, 0, 1) * SPAN_SLIDER_STEPS);
}

/**
 * The inverse: a slider position back to metres.
 *
 * Deliberately UNROUNDED. Rounding to the number field's 3 decimals here
 * quantised the low end of the track — at an Auto of 20 mm the bottom decade
 * is 2–20 mm, where whole ranges of positions collapse onto the same
 * millimetre and the thumb jumps as it is dragged. Display rounds; the scale
 * does not.
 */
export function spanFromSliderPosition(pos: number, autoSpanM: number): number {
  const { lo, hi } = spanSliderBounds(autoSpanM);
  const t = clamp(pos, 0, SPAN_SLIDER_STEPS) / SPAN_SLIDER_STEPS;
  return lo * (hi / lo) ** t;
}
